import { useGetPlanListingForQuote } from 'features/quote/api/getPlanListingForQuote'
import { PlanFormData, QuoteReviewProps, ServiceListing } from 'features/quote/components/type'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { wrapStyle } from 'views/Support/SupportForm'
import { Box, Flex } from 'components/elements'
import { Listing, MenuDropdown } from 'components/menuDropdown'
import { useEffect, useMemo, useState } from 'react'
import { ErrorBox } from 'features/profile/components/EditProfileForm'
import { Stages, TERMINOLOGY } from 'utils/enum'
import { ErrorCSS, Form, userRegHeadingCss } from 'views/style'
import { QouteLayout } from 'features/quote/page/GetAQuotes'
import { planScehema } from 'features/quote/components/GetAQuoteProcess/PlanSelection'
import { ChangePolicyFormButtons } from './PolicyCoveragePlan'
import useAuth from 'hooks/use-auth'
import { useGetMerchantAffiliateDetails } from 'features/quote/api/getmerchantAffiliateDetails'

const PolicyPlanSelection = (props: QuoteReviewProps) => {
  const { quoteUserReg, setQuoteUserReg, setStage, setOpenModal, planId } = props
  const [insurancePlan, setInsurancePlan] = useState<Listing>()
  const { user } = useAuth()
  const affiliateId = user?.affiliatedId
  const { data: planList, isLoading: isPlanListingLoading } = affiliateId ? useGetMerchantAffiliateDetails({ affiliateId }) : useGetPlanListingForQuote()
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<PlanFormData>({
    resolver: yupResolver(planScehema),
    defaultValues: {
      planId: (quoteUserReg && quoteUserReg.planId) || undefined,
    },
  })

  //all the policy into select lisiting format
  const insurancePlansList = useMemo(() => {
    return affiliateId ? planList?.data?.insurancePlans : planList?.data.plans || []
  }, [planList, affiliateId])

  const policyPlansList = useMemo(() => {
    if (insurancePlansList) {
      return (
        insurancePlansList
          //the listing should not include the current assign plan
          .filter((planList: { id: number }) => planList.id !== planId)
          .map(
            (
              planList: {
                name: string
                id: number
                basePremium: string
                maxCoverage: string
                primaryServiceId: ServiceListing
                primaryService: ServiceListing
                services: ServiceListing[]
                discount: number
              },
              index: number
            ) => {
              return {
                label: planList?.name,
                value: planList?.id,
                key: index,
                premiumAmount: planList?.basePremium,
                maxCoverage: planList?.maxCoverage,
                primaryService: affiliateId ? planList?.primaryService : planList?.primaryServiceId,
                services: planList?.services,
                discount: planList?.discount,
              }
            }
          )
      )
    }
  }, [affiliateId, insurancePlansList, planId])

  useEffect(() => {
    const plan = quoteUserReg && quoteUserReg?.planId

    if (policyPlansList) {
      const planDetails = policyPlansList.find((policy: { value: number }) => policy.value === plan)

      // Set the plan state with the matching label and value i already record exist
      if (planDetails) {
        setInsurancePlan({
          label: planDetails.label,
          value: planDetails.value,
          premiumAmount: planDetails.premiumAmount,
          maxCoverage: planDetails.maxCoverage,
          primaryService: planDetails.primaryService,
          services: planDetails.services,
          discount: planDetails?.discount,
        })
      }
    }
  }, [policyPlansList, quoteUserReg])

  const onSubmit = async (data: PlanFormData) => {
    const primaryService = insurancePlan?.primaryService
    //secondary service should not have the primary service
    const secondaryservices = insurancePlan?.services?.filter((service: { id: number }) => service.id !== primaryService?.id)
    const obj = {
      ...quoteUserReg,
      planId: data.planId,
      primaryService: primaryService,
      secondaryservices: secondaryservices,
      discount: insurancePlan?.discount,
    }

    setQuoteUserReg && setQuoteUserReg(obj)
    setStage && setStage(Stages.STAGE2)
  }

  return (
    <>
      <Flex align="center">
        <Flex as="h2" justify={'center'} css={userRegHeadingCss}>
          Change {TERMINOLOGY.warranty.singular}
        </Flex>
      </Flex>
      <Flex align="start" css={QouteLayout}>
        <Form onSubmit={handleSubmit(onSubmit)} css={{ width: '100%' }}>
          <Box css={{ textAlign: 'left' }}>
            <Box css={wrapStyle} style={{ width: '100%', paddingLeft: 0 }}>
              <Controller
                name={'planId'}
                control={control}
                render={({ field }) => (
                  <MenuDropdown
                    isDisabled={isPlanListingLoading}
                    isResponseFetching={isPlanListingLoading}
                    style={{ height: '140px' }}
                    {...field}
                    {...register('planId')}
                    label={`Select ${TERMINOLOGY.warranty.singular}`}
                    placeholder={`Select ${TERMINOLOGY.warranty.singular}`}
                    value={insurancePlan}
                    list={policyPlansList}
                    setValue={e => {
                      setInsurancePlan(e)
                      field.onChange(e.value)
                      setValue('planId', e.value)
                    }}
                  />
                )}
              />

              {!!errors.planId && <ErrorBox css={{ ...ErrorCSS, textAlign: 'left', mt: '$3' }}>{errors.planId?.message}</ErrorBox>}
            </Box>
            <Box as="h5" css={{ color: '$primary', textAlign: 'left', m: 0 }}></Box>
            <Box as="p" css={{ mt: '$2', mb: '$2' }}>
              <Box as="span" css={{ fontWeight: 'bold' }}>
                Note:
              </Box>
              If you change {TERMINOLOGY.warranty.singular.toLowerCase()}, your existing {TERMINOLOGY.warranty.singular.toLowerCase()} will be cancelled
            </Box>
          </Box>

          <ChangePolicyFormButtons setOpenModal={setOpenModal} setStage={setStage} setQuoteUserReg={setQuoteUserReg} />
        </Form>
      </Flex>
    </>
  )
}

export default PolicyPlanSelection
